import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clampFrameOffset,
  getFilmstripOffsets,
  THUMBNAIL_SCRUB_STEP_SECONDS,
} from '../../../utils/klydoThumbnail';
import useFilmstripFrames from './useFilmstripFrames';
import useScrubVideo from './useScrubVideo';

const FILMSTRIP_MATCH_TOLERANCE_SECONDS = THUMBNAIL_SCRUB_STEP_SECONDS / 2;

type UseKlydoThumbnailPickerProps = {
  open: boolean;
  loopUrl: string;
  offsetSeconds: number | null;
  durationInSeconds?: number;
  onConfirm: (offsetSeconds: number) => void;
};

const useKlydoThumbnailPicker = ({
  open,
  loopUrl,
  offsetSeconds,
  durationInSeconds,
  onConfirm,
}: UseKlydoThumbnailPickerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pendingSeekSecondsRef = useRef<number | null>(null);

  // Seeded from the stored duration so the filmstrip renders before the video loads.
  const [duration, setDuration] = useState<number>(durationInSeconds ?? 0);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [selectedOffsetSeconds, setSelectedOffsetSeconds] = useState<
    number | null
  >(offsetSeconds);

  const { scrubVideo, handleVideoError } = useScrubVideo({ open, loopUrl });

  // Nothing chosen yet starts from the middle of the loop — frame 0 is its seam.
  const previewOffsetSeconds = useMemo(
    () => clampFrameOffset(selectedOffsetSeconds ?? duration / 2, duration),
    [selectedOffsetSeconds, duration],
  );

  useEffect(() => {
    if (open) {
      setSelectedOffsetSeconds(offsetSeconds);
    } else {
      setIsVideoReady(false);
      pendingSeekSecondsRef.current = null;
    }
  }, [open, offsetSeconds]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }
    setDuration(video.duration);
    setIsVideoReady(true);
  }, []);

  // A seek issued while another is decoding restarts it, so a fast drag never
  // paints a frame. Only the newest target waits for the running seek.
  const seekPreview = useCallback((targetSeconds: number) => {
    const video = videoRef.current;
    if (!video) return;

    if (video.seeking) {
      pendingSeekSecondsRef.current = targetSeconds;
      return;
    }
    pendingSeekSecondsRef.current = null;
    video.pause();
    video.currentTime = targetSeconds;
  }, []);

  const handleSeeked = useCallback(() => {
    const pendingSeekSeconds = pendingSeekSecondsRef.current;
    pendingSeekSecondsRef.current = null;
    if (pendingSeekSeconds !== null) seekPreview(pendingSeekSeconds);
  }, [seekPreview]);

  useEffect(() => {
    if (!open || !isVideoReady) return;
    seekPreview(previewOffsetSeconds);
  }, [previewOffsetSeconds, isVideoReady, open, seekPreview]);

  const filmstripOffsetsSeconds = useMemo(
    () => getFilmstripOffsets(duration),
    [duration],
  );

  const filmstripFrames = useFilmstripFrames({
    loopUrl,
    scrubVideo,
    offsetsSeconds: filmstripOffsetsSeconds,
  });

  const selectedFilmstripOffsetSeconds = useMemo(
    () =>
      filmstripOffsetsSeconds.find(
        (frameOffsetSeconds) =>
          Math.abs(frameOffsetSeconds - previewOffsetSeconds) <=
          FILMSTRIP_MATCH_TOLERANCE_SECONDS,
      ),
    [filmstripOffsetsSeconds, previewOffsetSeconds],
  );

  const handleOffsetChange = useCallback((nextOffsetSeconds: number) => {
    setSelectedOffsetSeconds(nextOffsetSeconds);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(previewOffsetSeconds);
  }, [onConfirm, previewOffsetSeconds]);

  return {
    videoRef,
    videoUrl: scrubVideo?.url,
    duration,
    previewOffsetSeconds,
    filmstripFrames,
    selectedFilmstripOffsetSeconds,
    handleOffsetChange,
    handleLoadedMetadata,
    handleSeeked,
    handleVideoError,
    handleConfirm,
  };
};

export default useKlydoThumbnailPicker;
